import { OpenQuestionAnswersForItems } from '../../../../reducers/student/tracking/openQuestionAnswersReducer';
import { PreviouslySelectedOptionsForWidgets } from '../../../../reducers/student/tracking/questionWidgetTrackingReducer';
import { SemicolonSerializer } from '../serializer/Serializer';
import {
  CourseCompletionStatus,
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  TrackingDataVersion,
} from '../trackingClient';
import type { StudentApiClient, TrackingIdentifier } from '@ael/core-api';
import { Map, Set } from 'immutable';

type ServerLogData = {
  log: string;
  time: number;
};

export class ServerClient implements IPersistenceTrackingClient {
  private timeLastUpdated: number;
  private progressTrackingId: number;
  private timeTaken: number;
  private completedAt: number | null;
  private projectGuid: string;
  private apiClient: StudentApiClient;

  constructor(apiClient: StudentApiClient, projectGuid: string, currentDateNow: number = Date.now()) {
    this.timeLastUpdated = currentDateNow;
    this.projectGuid = projectGuid;
    this.apiClient = apiClient;
  }

  name = 'v360eStudentOnlineDbTracking';
  doesSupportLegacyData = true;

  async init(): Promise<boolean> {
    const initResult = await this.apiClient.initializeProgressTracking({ courseGuid: this.projectGuid });

    // Javascript can take any C# long if it is unix timestamp represented in seconds, which this is
    const completedAtUnixTimestampSeconds = initResult.completedAt ? (initResult.completedAt as number) : null;
    this.progressTrackingId = initResult.id;
    this.timeTaken = initResult.timeTaken;
    this.completedAt = completedAtUnixTimestampSeconds;
    return true;
  }

  async getTrackingData(): Promise<PersistenceTrackingData> {
    const progress = await this.apiClient.getTrackingData({ progressTrackingId: this.progressTrackingId });
    if (progress?.data === null) {
      return {
        finishedItems: Set<TrackingIdentifier>(),
        widgetTrackingData: Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>(),
        openQuestionAnswers: Map() as OpenQuestionAnswersForItems,
        version: TrackingDataVersion.v2,
      };
    }
    return SemicolonSerializer.deserializeData(progress.data);
  }

  async setTrackingData({
    widgetTrackingData,
    finishedItems,
    openQuestionAnswers,
  }: PersistenceTrackingData): Promise<void> {
    this.timeTaken = this._getTimeStudentSpentInCourse();
    const payload: ServerLogData = {
      log: SemicolonSerializer.serializeData(finishedItems, widgetTrackingData, openQuestionAnswers),
      time: this.timeTaken,
    };
    await this.apiClient.setTrackingData({
      progressTrackingId: this.progressTrackingId,
      log: payload.log,
      time: payload.time,
    });
    this.timeLastUpdated = Date.now();
  }

  async setCompletionStatus(status: CourseCompletionStatus): Promise<void> {
    if (this.completedAt === null && status === CourseCompletionStatus.Completed) {
      this.completedAt = Date.now();
      await this.apiClient.setCompletionStatus({ progressTrackingId: this.progressTrackingId, status });
    }
  }

  async setQuizStatus() {
    return Promise.resolve();
  }

  _getTimeStudentSpentInCourse(): number {
    return Date.now() - this.timeLastUpdated + this.timeTaken;
  }

  afterSetup(): Promise<void> {
    return Promise.resolve(undefined);
  }

  /**
   * Returns the progress tracking ID as the learner identifier.
   * This ID is unique per user per course, making it suitable for per-user features
   * like video progress tracking.
   */
  getLearnerId(): string {
    return this.progressTrackingId.toString();
  }
}
